import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useUpdateUserInfoMutation} from '../generated/graphql';
import {Analytics} from '../services/analytics';
import {useViewer} from '../state/viewer/hooks';
import {safeLocalStorage} from './localStorage';

export type ColorMode = 'light' | 'dark' | 'system';
const VALID_COLOR_SCHEMES: readonly ColorMode[] = ['light', 'dark', 'system'];

export type UseColorModeResult = {
  // The user's color scheme preference that is saved in their user info. Don't use this to determine dark mode.
  colorMode: ColorMode;

  // Update the user's color scheme preference.
  updateColorMode: (colorMode: ColorMode) => void;

  // A boolean indicating whether the current color scheme is actually rendered in dark mode.
  //
  // This is different than the user's preference, which is what we save. The user's preference
  // could be "system", which could be rendered in light mode or dark mode based on the OS's
  // color scheme.
  isDarkMode: boolean;
};

const PREFERS_DARK_MEDIA = window.matchMedia('(prefers-color-scheme: dark)');

const ColorModeContext = createContext<UseColorModeResult | null>(null);

export function ColorModeProvider({children}: {children: React.ReactNode}) {
  const viewer = useViewer();
  const [updateUserInfo, {data, loading}] = useUpdateUserInfoMutation();

  const [colorMode, setColorMode] = useState<ColorMode>(
    viewer?.userInfo?.colorMode ?? getStoredColorMode() ?? 'light'
  );
  const [osColorMode, setOsColorMode] = useState<ColorMode>(
    PREFERS_DARK_MEDIA.matches ? 'dark' : 'light'
  );

  const mutationColorMode = data?.updateUser?.user?.userInfo.colorMode;
  const viewerColorMode = viewer?.userInfo?.colorMode;

  useEffect(() => {
    // NOTE: Prioritize the mutation result over the user info result as it
    // should be the most updated value.
    if (mutationColorMode != null) {
      setColorMode(mutationColorMode);
    } else if (viewerColorMode != null) {
      setColorMode(viewerColorMode);
    }
  }, [mutationColorMode, viewerColorMode]);

  const isDarkMode = useMemo(
    () =>
      colorMode === 'system' ? osColorMode === 'dark' : colorMode === 'dark',
    [colorMode, osColorMode]
  );

  const updateColorMode = useCallback(
    (inColorMode: ColorMode) => {
      if (viewer?.userInfo == null) {
        return;
      }
      if (loading) {
        return;
      }
      if (!VALID_COLOR_SCHEMES.includes(inColorMode)) {
        Analytics.track('User unable to update color scheme', {
          colorMode: inColorMode,
          error: `${inColorMode} isn't a valid color scheme. Valid options are: ${VALID_COLOR_SCHEMES.join(
            ', '
          )}.`,
        });
        return;
      }

      // Optimistically update the color scheme state...
      setColorMode(inColorMode);

      // ...and then update the user info in the backend.
      updateUserInfo({
        variables: {
          userInfo: JSON.stringify({
            ...viewer?.userInfo,
            colorMode: inColorMode,
          }),
        },
      });
      Analytics.track('User updated color scheme', {
        colorMode: inColorMode,
      });
    },
    [updateUserInfo, viewer?.userInfo, loading]
  );

  // Watch for keyboard shortcut and OS color scheme changes
  useEffect(() => {
    const watchKeyboardShortcut = (event: KeyboardEvent) => {
      if (loading) {
        return;
      }
      // event.altKey - pressed Option key on Macs
      // event.ctrlKey - pressed Control key on Linux or Windows
      if ((event.altKey || event.ctrlKey) && event.code === 'KeyM') {
        event.preventDefault();

        if (isDarkMode) {
          updateColorMode('light');
        } else {
          updateColorMode('dark');
        }
      }
    };
    function watchDarkOSMedia({matches}: MediaQueryListEvent) {
      setOsColorMode(matches ? 'dark' : 'light');
    }

    document.addEventListener('keydown', watchKeyboardShortcut);
    PREFERS_DARK_MEDIA.addEventListener('change', watchDarkOSMedia);

    return () => {
      document.removeEventListener('keydown', watchKeyboardShortcut);
      PREFERS_DARK_MEDIA.removeEventListener('change', watchDarkOSMedia);
    };
  }, [isDarkMode, loading, updateColorMode]);

  // Apply dark mode to the DOM and persist to localStorage.
  useEffect(() => {
    if (isDarkMode) {
      /**
       * @note This adds the 'night-mode' class to the <html> element. If we add the class to a different element,
       * the night mode css filter breaks position:fixed components, e.g. <ViewBar>.
       *
       * Google 'css filter position fixed' for more details. Surprising fix found here:
       * @link https://developpaper.com/explain-the-reasons-and-solutions-of-the-conflict-between-filter-and-fixed-in-detail/
       */
      document.documentElement.classList.add('night-mode');
      // Used for tailwind dark mode
      document.documentElement.setAttribute('data-mode', 'dark');

      // The data attribute used by @wandb/components to toggle dark mode
      document.documentElement.setAttribute('data-dark-mode', 'true');
      safeLocalStorage.setItem('__wb_theme', 'dark');
    } else {
      document.documentElement.classList.remove('night-mode');
      document.documentElement.removeAttribute('data-mode');
      document.documentElement.removeAttribute('data-dark-mode');
      safeLocalStorage.setItem('__wb_theme', 'light');
    }
  }, [isDarkMode]);

  const value = useMemo(
    () => ({
      colorMode,
      updateColorMode,
      isDarkMode,
    }),
    [colorMode, updateColorMode, isDarkMode]
  );

  return (
    <ColorModeContext.Provider value={value}>
      {children}
    </ColorModeContext.Provider>
  );
}

export function useColorMode(): UseColorModeResult {
  const context = useContext(ColorModeContext);
  if (context == null) {
    return {
      colorMode: 'light',
      updateColorMode: () => {},
      isDarkMode: false,
    };
  }
  return context;
}

/**
 * getStoredColorMode reads the stored color mode from local storage. This is
 * used as a fallback for the initial render before the viewer query resolves.
 */
function getStoredColorMode(): ColorMode | null {
  // NOTE: This value matches what's read in 'index.html'.
  const stored = safeLocalStorage.getItem('__wb_theme');
  return stored === 'dark' || stored === 'light' ? stored : null;
}
